// An independent background-only displacement pass. Subject artwork and rig masks
// never enter this renderer; protection zones also cover their complete sweeps.
export function createBackgroundMotion(base,sourcePixels,objects){
 const width=base.width,height=base.height;
 const output=document.createElement('canvas');output.width=width;output.height=height;
 const gl=output.getContext('webgl',{alpha:false,antialias:false,depth:false,stencil:false,preserveDrawingBuffer:true});
 if(!gl)return {draw:()=>base,available:false};
 const mask=document.createElement('canvas');mask.width=width;mask.height=height;
 const m=mask.getContext('2d'),pixels=m.createImageData(width,height);
 const lake=new Path2D('M380 402 L1265 395 L1260 530 L1100 551 L790 535 L435 490 Z');
 for(let y=0;y<height;y++)for(let x=0;x<width;x++){
  const i=(y*width+x)*4,r=sourcePixels[i],g=sourcePixels[i+1],b=sourcePixels[i+2];
  const green=g>30&&g>b*.85&&(g>r*1.14||g>b*1.18)&&(g<185||b<g*.85);
  const water=y>395&&y<551&&b>r*1.15&&g>r*1.2&&b>g*.98&&m.isPointInPath(lake,x,y);
  pixels.data[i]=green?255:0;pixels.data[i+1]=water?255:0;pixels.data[i+3]=255;
 }
 m.putImageData(pixels,0,0);
 const feather=document.createElement('canvas');feather.width=width;feather.height=height;
 const f=feather.getContext('2d');f.filter='blur(5px)';f.drawImage(mask,0,0);
 m.clearRect(0,0,width,height);m.drawImage(feather,0,0);
 m.fillStyle='black';
 for(const {box:[x,y,w,h]}of objects)m.fillRect(x-12,y-12,w+24,h+24);
 // Do not move clean plates exposed by head, stick, wing, or warming-arm motion.
 m.fillRect(490,120,505,570);m.fillRect(90,420,185,185);
 const compile=(type,source)=>{
  const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);
  if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(shader));
  return shader;
 };
 const program=gl.createProgram();
 gl.attachShader(program,compile(gl.VERTEX_SHADER,`attribute vec2 position;varying vec2 uv;void main(){uv=(position+1.0)*.5;gl_Position=vec4(position,0.,1.);}`));
 gl.attachShader(program,compile(gl.FRAGMENT_SHADER,`
 precision highp float;
 varying vec2 uv;uniform sampler2D art;uniform sampler2D regions;uniform vec2 size;uniform float time;
 void main(){
  vec2 p=uv*size;vec2 weight=texture2D(regions,uv).rg;
  float breeze=sin(time*.73+p.y*.017+p.x*.009)-sin(p.y*.017+p.x*.009);
  float rustle=sin(time*1.13+p.x*.023)-sin(p.x*.023);
  float ripple=sin(time*.82+p.y*.19)-sin(p.y*.19);
  vec2 shift=vec2((breeze*1.8+rustle*.48)*weight.r+ripple*2.4*weight.g,breeze*.45*weight.r);
  gl_FragColor=texture2D(art,uv+shift/size);
 }`));
 gl.linkProgram(program);
 if(!gl.getProgramParameter(program,gl.LINK_STATUS))return {draw:()=>base,available:false};
 gl.useProgram(program);
 const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
 gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
 const position=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(position);gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
 gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
 for(const [unit,name,image]of [[0,'art',base],[1,'regions',mask]]){
  gl.activeTexture(gl.TEXTURE0+unit);gl.bindTexture(gl.TEXTURE_2D,gl.createTexture());
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,image);gl.uniform1i(gl.getUniformLocation(program,name),unit);
 }
 gl.uniform2f(gl.getUniformLocation(program,'size'),width,height);
 const time=gl.getUniformLocation(program,'time');gl.viewport(0,0,width,height);
 const cached=document.createElement('canvas');cached.width=width;cached.height=height;
 const cache=cached.getContext('2d',{alpha:false});
 let lastTick=null;
 return {available:true,draw(ms){
  if(gl.isContextLost())return base;
  // Slow ambient drift needs only eight updates per second. Reuse a 2D surface
  // between updates so the main loop does not synchronize GPU contexts at 30Hz.
  const tick=Math.floor(ms/125);
  if(tick!==lastTick){gl.uniform1f(time,tick*.125);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);cache.drawImage(output,0,0);lastTick=tick;}
  return cached;
 }};
}
